import logo from 'assets/header-prevencor-xx.png';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import fetchImageBase64 from 'utils/helpers';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/** Largura máx. da imagem no PDF (~A4 a 500pt). Evita RangeError no pdfmake (DR-ORNELLAS-25M). */
const ECG_PDF_MAX_WIDTH = 1600;
const ECG_PDF_JPEG_QUALITY = 0.82;

/**
 * Redimensiona e converte para JPEG antes do pdfmake.
 * PNG full-res (ex.: BMP convertido) faz Buffer.alloc(pixelCount) estourar memória.
 */
export async function optimizeEcgImageForPdf(
  base64Input,
  { maxWidth = ECG_PDF_MAX_WIDTH, quality = ECG_PDF_JPEG_QUALITY } = {}
) {
  if (!base64Input) return null;

  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        if (!width || !height) {
          reject(new Error('Dimensões de imagem inválidas'));
          return;
        }

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas 2D indisponível'));
          return;
        }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Falha ao carregar imagem do ECG'));
    img.src = base64Input;
  });
}

/** @deprecated use optimizeEcgImageForPdf — mantido para callers externos */
export async function convertBmpToPng(base64Bmp) {
  return optimizeEcgImageForPdf(base64Bmp);
}

async function normalizeEcgImageBase64(base64Input) {
  if (!base64Input) return null;
  // Sem fallback para o original: imagem enorme → RangeError no pdfmake (DR-ORNELLAS-25M)
  return optimizeEcgImageForPdf(base64Input);
}

function buildObservacaoContent(observacao) {
  const text = String(observacao ?? '').trim();
  if (!text) {
    return [{ text: 'Sem descrição preenchida.', italics: true, color: '#666666' }];
  }

  return text.split('\n').map((line) => ({ text: line || ' ', margin: [0, 0, 0, 0] }));
}

export async function generateEcgPdf({ ecg, currentImageBase64 }) {
  try {
    const headerBase64 = await fetchImageBase64(logo);
    const ecgImageBase64 = await normalizeEcgImageBase64(currentImageBase64);
    const hashAssinatura = ecg?.HASH_INTEGRIDADE;

    const content = [
      ...(ecgImageBase64
        ? [
            {
              image: ecgImageBase64,
              width: 475,
              alignment: 'center',
              margin: [0, 0, 0, 0],
            },
          ]
        : []),
      {
        text: 'Descrição do Exame:',
        fontSize: 12,
        bold: true,
        decoration: 'underline',
        margin: [0, 0, 0, 0],
      },
      ...buildObservacaoContent(ecg?.OBSERVACAO),
      {
        text: 'Conferido e assinado eletronicamente por:',
        italics: true,
        alignment: 'center',
        margin: [0, 10, 0, 0],
      },
      {
        text: `Assinatura Digital: ${hashAssinatura}`,
        bold: true,
        italics: true,
        alignment: 'center',
        fontSize: 9,
        margin: [0, 0, 0, 0],
      },
      {
        text: `Médico: Dr. ${ecg?.REALIZOU || '-'} - CRM: ${ecg?.CRMMED || '-'}`,
        alignment: 'center',
        margin: [0, 0, 0, 0],
      },
    ];

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 50, 40, 0],
      header: function () {
        return {
          stack: [
            {
              image: headerBase64,
              width: 450,
              alignment: 'center',
              margin: [0, 0, 0, 0],
            },
          ],
        };
      },
      content,
      defaultStyle: { fontSize: 10 },
    };

    return pdfMake.createPdf(docDefinition);
  } catch {
    throw new Error('Erro ao gerar PDF do ECG!');
  }
}
